
import { supabase } from '../supabase';
import { BundleItem, Product } from '../../types';
import { uploadFile } from './shared';

// Helper to map DB columns (snake_case) to App types (camelCase)
const mapProduct = (row: any): Product => ({
    id: row.id,
    name: row.name,
    description: row.description,
    price: row.price,
    imageUrl: row.image_url || row.imageUrl || '', 
    images: row.images || [], 
    classProfit: row.class_profit || row.classProfit || 0,
    purchasePrice: row.purchase_price || 0, // Renamed from company_profit
    variants: row.variants || [],
    weightGrams: row.weight_grams ?? undefined,
    lengthCm: row.length_cm != null ? Number(row.length_cm) : undefined,
    widthCm: row.width_cm != null ? Number(row.width_cm) : undefined,
    heightCm: row.height_cm != null ? Number(row.height_cm) : undefined,
    labelUrl: row.label_url || undefined,
    categoryId: row.category_id || undefined,
    fixedCommissionRate: row.fixed_commission_rate != null ? Number(row.fixed_commission_rate) : undefined,
    bundleItems: Array.isArray(row.bundle_items) && row.bundle_items.length > 0 ? row.bundle_items : undefined,
    visibleClassIds: Array.isArray(row.visible_class_ids) && row.visible_class_ids.length > 0 ? row.visible_class_ids : undefined,
    subtitle: row.subtitle || undefined,
    seasonTag: row.season_tag || undefined,
    stockStatus: ['in_stock', 'low', 'very_low', 'out'].includes(row.stock_status) ? row.stock_status : 'in_stock',
    highlights: Array.isArray(row.highlights) ? row.highlights.filter((h: unknown) => typeof h === 'string' && h.trim()) : [],
    relatedProductIds: Array.isArray(row.related_product_ids) ? row.related_product_ids : [],
});

// Butikens fält (shopsidan och produktsidan). Skickas bara när de finns i ändringen.
const shopColumns = (p: Partial<Product>) => {
    const out: Record<string, unknown> = {};
    if ('subtitle' in p) out.subtitle = p.subtitle?.trim() || null;
    if ('seasonTag' in p) out.season_tag = p.seasonTag?.trim() || null;
    if ('stockStatus' in p) out.stock_status = p.stockStatus || 'in_stock';
    if ('highlights' in p) out.highlights = (p.highlights || []).map(h => h.trim()).filter(Boolean).slice(0, 5);
    if ('relatedProductIds' in p) out.related_product_ids = (p.relatedProductIds || []).slice(0, 4);
    return out;
};

// Tomt fält sparas som null, så att ett borttaget värde också töms i databasen.
const packageColumns = (p: Partial<Product>) => ({
    weight_grams: p.weightGrams ?? null,
    length_cm: p.lengthCm ?? null,
    width_cm: p.widthCm ?? null,
    height_cm: p.heightCm ?? null,
});

export const getProducts = async (): Promise<Product[]> => {
    try {
        const { data, error } = await supabase
            .from('products')
            .select('*')
            .order('name');
        
        if (error) {
            console.warn("Product Fetch Warning:", error.message);
            return []; // Return empty list on RLS error so page doesn't crash
        }
        return (data || []).map(mapProduct);
    } catch (e) {
        console.error("Critical Product Error:", e);
        return [];
    }
};

export const addProduct = async (product: Partial<Product>) => {
    // Explicitly map fields to avoid sending camelCase keys
    const dbPayload = {
        name: product.name,
        description: product.description,
        price: product.price,
        image_url: product.imageUrl,
        images: product.images,
        class_profit: product.classProfit,
        purchase_price: product.purchasePrice, // New column name
        variants: product.variants,
        ...packageColumns(product),
        label_url: product.labelUrl || null,
        category_id: product.categoryId || null,
        fixed_commission_rate: product.fixedCommissionRate || null,
        bundle_items: product.bundleItems?.length ? product.bundleItems : null,
        visible_class_ids: product.visibleClassIds?.length ? product.visibleClassIds : null,
        ...shopColumns(product),
    };
    
    const { error } = await supabase.from('products').insert([dbPayload]);
    if (error) throw error;
};

export const updateProduct = async (id: string, updates: Partial<Product>) => {
    const dbPayload: any = {};
    
    if (updates.name !== undefined) dbPayload.name = updates.name;
    if (updates.description !== undefined) dbPayload.description = updates.description;
    if (updates.price !== undefined) dbPayload.price = updates.price;
    if (updates.imageUrl !== undefined) dbPayload.image_url = updates.imageUrl;
    if (updates.images !== undefined) dbPayload.images = updates.images;
    if (updates.classProfit !== undefined) dbPayload.class_profit = updates.classProfit;
    if (updates.purchasePrice !== undefined) dbPayload.purchase_price = updates.purchasePrice; // New column name
    if (updates.variants !== undefined) dbPayload.variants = updates.variants;
    // Tom länk sparas som null: då går etiketten till butiken.
    if ('labelUrl' in updates) dbPayload.label_url = updates.labelUrl || null;
    // Kategorin bestämmer momsen. Utan kategori visas produkten inte i butiken.
    if ('categoryId' in updates) dbPayload.category_id = updates.categoryId || null;
    // Bonussystemet: fast provisionssats (t.ex. 0.5). Tomt = trappan 35/40/45 %.
    if ('fixedCommissionRate' in updates) dbPayload.fixed_commission_rate = updates.fixedCommissionRate || null;
    // Paket: innehållet. Tomt = vanlig produkt.
    if ('bundleItems' in updates) dbPayload.bundle_items = updates.bundleItems?.length ? updates.bundleItems : null;
    // Grupperna som ser produkten. Tomt = alla.
    if ('visibleClassIds' in updates) dbPayload.visible_class_ids = updates.visibleClassIds?.length ? updates.visibleClassIds : null;
    Object.assign(dbPayload, shopColumns(updates));
    // Vikt och mått skickas alltid tillsammans från produktredigeraren.
    if ('weightGrams' in updates || 'lengthCm' in updates || 'widthCm' in updates || 'heightCm' in updates) {
        Object.assign(dbPayload, packageColumns(updates));
    }

    const { error } = await supabase.from('products').update(dbPayload).eq('id', id);
    if (error) throw error;
};

export const deleteProduct = async (id: string) => {
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) throw error;
};

/** Admin: fraktpriset för ett paket som inte är sparat än (preview_bundle_shipping). */
export type BundleShippingPreview =
    | { ok: true; price: number; listPrice: number; floorUsed: boolean; weightGrams: number; packaging: 'BAG' | 'BOX' }
    | { ok: false; reason: string };

export const previewBundleShipping = async (price: number, items: BundleItem[], fixedRate?: number | null): Promise<BundleShippingPreview> => {
    const { data, error } = await supabase.rpc('preview_bundle_shipping', { p_price: price, p_items: items, p_fixed_rate: fixedRate ?? null });
    if (error) return { ok: false, reason: error.message };
    if (!data?.ok) return { ok: false, reason: data?.reason || 'Fraktpriset kunde inte räknas.' };
    return { ok: true, price: Number(data.price), listPrice: Number(data.listPrice), floorUsed: !!data.floorUsed, weightGrams: Number(data.weightGrams), packaging: data.packaging };
};

export const uploadProductImage = async (file: File): Promise<string> => {
    return uploadFile('products', file);
};

/**
 * De mest sålda produkterna, bästa först — bara ordningen, inga antal
 * (butiken är publik). Saknas funktionen i databasen blir listan tom och
 * Bästsäljare visar admins val i stället.
 */
export const getBestsellingProductIds = async (days = 30, limit = 6): Promise<string[]> => {
    const { data, error } = await supabase.rpc('get_bestselling_product_ids', { p_days: days, p_limit: limit });
    if (error || !Array.isArray(data)) return [];
    return data.map((row: any) => String(row.product_id ?? row));
};
